import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import LanguageSwitcher from './components/common/LanguageSwitcher';

import Layout from './pages/Layout';
import AdminLayout from './pages/AdminLayout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AdminDashboard from './pages/AdminDashboard';
import AdminSurveyManagement from './pages/AdminSurveyManagement';
import AdminDocumentManagement from './pages/AdminDocumentManagement';
import AdminPolicyManagement from './pages/AdminPolicyManagement';
import AdminDigitalLibrary from './pages/AdminDigitalLibrary';
import DigitalLibrary from './pages/DigitalLibrary';
import AdminOnboardingManagement from './pages/AdminOnboardingManagement';
import AdminUserManagement from './pages/AdminUserManagement';
import AdminGrievanceManagement from './pages/AdminGrievanceManagement';
import AdminPOSHManagement from './pages/AdminPOSHManagement';
import AdminDocumentRequirements from './pages/AdminDocumentRequirements';
import AdminUserHierarchy from './pages/AdminUserHierarchy';
import AdminEmployeeManagement from './pages/AdminEmployeeManagement';
import Onboarding from './pages/Onboarding';
import ExistingEmployee from './pages/ExistingEmployee';
import AdminExistingEmployee from './pages/AdminExistingEmployee';
import Offboarding from './pages/Offboarding';
import AdminOffboarding from './pages/AdminOffboarding';
import ITOnboardingForm from './pages/ITOnboardingForm';
import ITOnboardingFormDetails from './pages/ITOnboardingFormDetails';
import OnboardingForm from './pages/OnboardingForm';
import AccessProvisioningManagement from './pages/AccessProvisioningManagement';
import ResignationSharedView from './pages/ResignationSharedView';
import MotionDemo from './pages/MotionDemo';

import theme from './context/theme';
import SurveyPage from './pages/SurveyPage';
import AdminDepartmentMaster from './pages/AdminDepartmentMaster';
import AdminEmployeeDocumentsReport from './pages/AdminEmployeeDocumentsReport';
import AdminPerformanceManagement from './pages/AdminPerformanceManagement';
import EmployeePerformanceForm from './pages/EmployeePerformanceForm';
import ManagerPerformanceReview from './pages/ManagerPerformanceReview';
import ManagerKpiProposals from './pages/ManagerKpiProposals';
import HRPerformanceView from './pages/HRPerformanceView';
import SignatureGenerator from './pages/SignatureGenerator';
import AdminJoineeBanner from './pages/AdminJoineeBanner';
import ExpenseMasters from './pages/expense/ExpenseMasters';
import MyExpenses from './pages/expense/MyExpenses';
import ExpenseVoucherForm from './pages/expense/ExpenseVoucherForm';
import ExpenseVoucherDetails from './pages/expense/ExpenseVoucherDetails';
import ExpenseEmployeeRoute from './components/expense/ExpenseEmployeeRoute';
import ExpenseApprovals from './pages/expense/ExpenseApprovals';
import ExpenseApprovalReview from './pages/expense/ExpenseApprovalReview';
import ExpenseApprovalRoute from './components/expense/ExpenseApprovalRoute';
import ExpenseAccountsRoute from './components/expense/ExpenseAccountsRoute';
import AccountsExpenseGrid from './pages/expense/AccountsExpenseGrid';
import AccountsVoucherDetail from './pages/expense/AccountsVoucherDetail';
import AccountsVoucherRegister from './pages/expense/AccountsVoucherRegister';
import AccountsReportsDashboard from './pages/expense/AccountsReportsDashboard';
import AccountsPayablesReport from './pages/expense/AccountsPayablesReport';
import AccountsPaymentRegister from './pages/expense/AccountsPaymentRegister';
import AccountsWalletReport from './pages/expense/AccountsWalletReport';
import AccountsAdvancesReport from './pages/expense/AccountsAdvancesReport';
import AccountsBulkBills from './pages/expense/AccountsBulkBills';
import AccountsWallets from './pages/expense/AccountsWallets';
import MyWallet from './pages/expense/MyWallet';
import MyExpenseDashboard from './pages/expense/MyExpenseDashboard';
import AccountsReminders from './pages/expense/AccountsReminders';
import ExpenseVoucherPrint from './pages/expense/ExpenseVoucherPrint';
// import Testing from './pages/testing';
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return <div>Loading...</div>;
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  return user && user.primaryRole === 'employee' ? children : <Navigate to="/admin/dashboard" replace />;
};

const AdminRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return <div>Loading...</div>;
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  return user && (user.primaryRole === 'admin' || user.primaryRole === 'itsupport') ? children : <Navigate to="/dashboard" replace />;
};

const ITSupportRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return <div>Loading...</div>;
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  return user && user.primaryRole === 'itsupport' ? children : <Navigate to="/dashboard" replace />;
};

const AdminOnlyRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return <div>Loading...</div>;
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  return user && user.primaryRole === 'admin' ? children : <Navigate to="/dashboard" replace />;
};

// Expense masters: HR admin or users with Expense Accounts access (UI only; backend enforces)
const ExpenseMastersRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user || user.access === undefined) return <div>Loading...</div>;
  const allowed = user.primaryRole === 'admin' || user.access?.expenseAccounts === true;
  return allowed ? children : <Navigate to={user.primaryRole === 'employee' ? '/dashboard' : '/admin/dashboard'} replace />;
};

// Employee Expense vouchers, mounted under both layouts (/expenses, /admin/expenses)
const expenseRoutes = (
  <>
    <Route path="expenses" element={<ExpenseEmployeeRoute><MyExpenses /></ExpenseEmployeeRoute>} />
    <Route path="expense-dashboard" element={<ExpenseEmployeeRoute><MyExpenseDashboard /></ExpenseEmployeeRoute>} />
    <Route path="expenses/new" element={<ExpenseEmployeeRoute><ExpenseVoucherForm /></ExpenseEmployeeRoute>} />
    <Route path="expenses/:id" element={<ExpenseEmployeeRoute><ExpenseVoucherDetails /></ExpenseEmployeeRoute>} />
    <Route path="expenses/:id/edit" element={<ExpenseEmployeeRoute><ExpenseVoucherForm /></ExpenseEmployeeRoute>} />
    <Route path="expense-approvals" element={<ExpenseApprovalRoute><ExpenseApprovals /></ExpenseApprovalRoute>} />
    <Route path="expense-approvals/:id" element={<ExpenseApprovalRoute><ExpenseApprovalReview /></ExpenseApprovalRoute>} />
    <Route path="expense-wallet" element={<ExpenseEmployeeRoute><MyWallet /></ExpenseEmployeeRoute>} />
    <Route path="expense-accounts" element={<ExpenseAccountsRoute><AccountsExpenseGrid /></ExpenseAccountsRoute>} />
    <Route path="expense-accounts/register" element={<ExpenseAccountsRoute><AccountsVoucherRegister /></ExpenseAccountsRoute>} />
    <Route path="expense-accounts/reports" element={<ExpenseAccountsRoute><AccountsReportsDashboard /></ExpenseAccountsRoute>} />
    <Route path="expense-accounts/reports/payables" element={<ExpenseAccountsRoute><AccountsPayablesReport /></ExpenseAccountsRoute>} />
    <Route path="expense-accounts/reports/payments" element={<ExpenseAccountsRoute><AccountsPaymentRegister /></ExpenseAccountsRoute>} />
    <Route path="expense-accounts/reports/wallets" element={<ExpenseAccountsRoute><AccountsWalletReport /></ExpenseAccountsRoute>} />
    <Route path="expense-accounts/reports/advances" element={<ExpenseAccountsRoute><AccountsAdvancesReport /></ExpenseAccountsRoute>} />
    <Route path="expense-accounts/reports/bills" element={<ExpenseAccountsRoute><AccountsBulkBills /></ExpenseAccountsRoute>} />
    <Route path="expense-accounts/reports/reminders" element={<ExpenseAccountsRoute><AccountsReminders /></ExpenseAccountsRoute>} />
    <Route path="expense-accounts/:id" element={<ExpenseAccountsRoute><AccountsVoucherDetail /></ExpenseAccountsRoute>} />
    <Route path="expense-wallets" element={<ExpenseAccountsRoute><AccountsWallets /></ExpenseAccountsRoute>} />
  </>
);

const PublicRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated() ? <Navigate to="/login" replace /> : children;
};

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div>Loading...</div>;
  return (

    <Routes>
   
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      
      {/* Public Routes - No Authentication Required */}
      <Route path="/itonboarding-form" element={<ITOnboardingForm />} />
      <Route path="/itonboarding-form/:id" element={<ITOnboardingFormDetails />} />
      <Route path="/onboarding-form" element={<OnboardingForm />} />
      <Route path="/onboarding-form/:id" element={<OnboardingForm />} />
      <Route path="/resignation-view/:token" element={<ResignationSharedView />} />
      <Route path="/motion-demo" element={<MotionDemo />} />


         {/* <Route path="/testing" element={<Testing />} />  */}
      
      <Route path="/" element={<Navigate to="/existing" replace />} />
      
      {/* Regular User Routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="onboarding" element={<Onboarding />} />
        <Route path="existing" element={<ExistingEmployee />} />
        <Route path="offboarding" element={<Offboarding />} />
        <Route path="performance-review" element={<EmployeePerformanceForm />} />
        <Route path="manager/reviews" element={<ManagerPerformanceReview />} />
        <Route path="manager/kpi-proposals" element={<ManagerKpiProposals />} />
        <Route path="digital-library" element={<DigitalLibrary />} />
        <Route path="signature-generator" element={<SignatureGenerator />} />
        <Route path="expense-masters" element={<ExpenseMastersRoute><ExpenseMasters /></ExpenseMastersRoute>} />
        {expenseRoutes}
      </Route>

      
      {/* Expense voucher print view: outside the menu layouts so it prints cleanly.
          The server authorizes (owner / Accounts / routed approver). */}
      <Route path="/expense-print/:id" element={
        <ProtectedRoute>
          <ExpenseVoucherPrint />
        </ProtectedRoute>
      } />

      {/* Survey Route - Direct access */}
      <Route path="/survey/:surveyId" element={
        <ProtectedRoute>
          <SurveyPage />
        </ProtectedRoute>
      } />

      {/* Admin Existing Employee Route - Standalone */}


      {/* Admin Routes */}
      <Route path="/admin" element={
        <AdminRoute>
          <AdminLayout />
        </AdminRoute>
      }>
        <Route path="admin-existing" element={<AdminExistingEmployee />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="users" element={<AdminOnlyRoute><AdminUserManagement /></AdminOnlyRoute>} />
        <Route path="hierarchy" element={<AdminOnlyRoute><AdminUserHierarchy /></AdminOnlyRoute>} />
        <Route path="onboarding" element={<AdminOnboardingManagement />} />
        <Route path="employees" element={<AdminOnlyRoute><AdminEmployeeManagement /></AdminOnlyRoute>} />
        <Route path="offboarding" element={<AdminOnlyRoute><AdminOffboarding /></AdminOnlyRoute>} />
        <Route path="documents" element={<AdminOnlyRoute><AdminDocumentManagement /></AdminOnlyRoute>} />
        <Route path="policies" element={<AdminOnlyRoute><AdminPolicyManagement /></AdminOnlyRoute>} />
        <Route path="digital-library" element={<AdminOnlyRoute><AdminDigitalLibrary /></AdminOnlyRoute>} />
        <Route path="document-requirements" element={<AdminOnlyRoute><AdminDocumentRequirements /></AdminOnlyRoute>} />
        <Route path="surveys" element={<AdminOnlyRoute><AdminSurveyManagement /></AdminOnlyRoute>} />
        <Route path="grievances" element={<AdminOnlyRoute><AdminGrievanceManagement /></AdminOnlyRoute>} />
        <Route path="posh" element={<AdminOnlyRoute><AdminPOSHManagement /></AdminOnlyRoute>} />
        <Route path="access-provisioning" element={<AdminOnlyRoute><AccessProvisioningManagement /></AdminOnlyRoute>} />
        <Route path="reports/employee-documents" element={<AdminOnlyRoute><AdminEmployeeDocumentsReport /></AdminOnlyRoute>} />
        <Route path="performance-management" element={<AdminOnlyRoute><AdminPerformanceManagement /></AdminOnlyRoute>} />
        <Route path="performance-reviews" element={<AdminOnlyRoute><HRPerformanceView /></AdminOnlyRoute>} />
        <Route path="manager/reviews" element={<ManagerPerformanceReview />} />
        <Route path="manager/kpi-proposals" element={<ManagerKpiProposals />} />
        <Route path="joinee-banner" element={<AdminOnlyRoute><AdminJoineeBanner /></AdminOnlyRoute>} />
        <Route path="settings" element={<AdminOnlyRoute><div>Admin System Settings</div></AdminOnlyRoute>} />
        <Route path="department-master" element={<AdminDepartmentMaster />} />
        <Route path="expense-masters" element={<ExpenseMastersRoute><ExpenseMasters /></ExpenseMastersRoute>} />
        {expenseRoutes}
      </Route>
      
      <Route path="*"element={<AdminOnlyRoute><AdminUserManagement /></AdminOnlyRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <ToastContainer 
            position="top-right" 
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            limit={3}
          />
          {/* Global Google Translate language selector (floating, bottom-left) */}
          <LanguageSwitcher />
          <AppRoutes />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
